import React, { useState, useCallback, useRef } from 'react';
import { generateImage } from '@/services/geminiService';
import { useCustomDesign } from '@/contexts/CustomDesignContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Upload, Wand2, Download, Package } from 'lucide-react';
import { toast } from 'sonner';

type AspectRatio = "1:1" | "3:4" | "4:3" | "9:16" | "16:9";

const productMockups = {
  tshirt: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=500&h=600&fit=crop',
  hoodie: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=500&h=600&fit=crop',
  mug: 'https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?w=500&h=600&fit=crop',
  poster: 'https://images.unsplash.com/photo-1513519245088-0e12902e35ca?w=500&h=600&fit=crop',
};

type ProductType = keyof typeof productMockups;

const LoadingSpinner: React.FC = () => (
  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-20 rounded-lg">
    <div className="flex flex-col items-center gap-3">
      <Loader2 className="h-12 w-12 animate-spin text-white" />
      <p className="text-white text-sm font-medium">Generating your logo...</p>
    </div>
  </div>
);

export const LogoGenerator: React.FC = () => {
  const [selectedProduct, setSelectedProduct] = useState<ProductType>('tshirt');
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('1:1');
  const [generatedLogo, setGeneratedLogo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { addDesign } = useCustomDesign();

  const handleGenerateLogo = useCallback(async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a description for your logo');
      return;
    }

    setIsLoading(true);
    setGeneratedLogo(null);

    try {
      const imageUrl = await generateImage(prompt, aspectRatio);
      setGeneratedLogo(imageUrl);
      toast.success('Logo generated successfully!');
    } catch (error) {
      console.error('Logo generation error:', error);
      toast.error('Failed to generate logo. Please check your API key and try again.');
    } finally {
      setIsLoading(false);
    }
  }, [prompt, aspectRatio]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setGeneratedLogo(reader.result as string);
        setPrompt(`Uploaded: ${file.name}`);
        toast.success('Image uploaded successfully!');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveDesign = useCallback(() => {
    if (!generatedLogo) {
      toast.error('No design to save');
      return;
    }

    const design = addDesign({
      imageUrl: generatedLogo,
      prompt: prompt,
      productId: selectedProduct,
    });

    toast.success('Design saved! You can now apply it to products.');
  }, [generatedLogo, prompt, selectedProduct, addDesign]);

  const handleDownloadLogo = useCallback(() => {
    if (!generatedLogo) return;

    const link = document.createElement('a');
    link.href = generatedLogo;
    link.download = `logo-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Logo downloaded!');
  }, [generatedLogo]);

  return (
    <div className="container mx-auto p-6 space-y-8">
      <div className="text-center space-y-2">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          AI Logo Generator
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Create stunning custom logos with AI or upload your own design
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Controls Panel */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Design Your Logo
            </CardTitle>
            <CardDescription>
              Describe your vision or upload an existing design
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Product Selection */}
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Package className="h-4 w-4" />
                Target Product
              </label>
              <Select value={selectedProduct} onValueChange={(value) => setSelectedProduct(value as ProductType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tshirt">T-Shirt</SelectItem>
                  <SelectItem value="hoodie">Hoodie</SelectItem>
                  <SelectItem value="mug">Mug</SelectItem>
                  <SelectItem value="poster">Poster</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Aspect Ratio */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Aspect Ratio</label>
              <Select value={aspectRatio} onValueChange={(value) => setAspectRatio(value as AspectRatio)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1:1">Square (1:1)</SelectItem>
                  <SelectItem value="3:4">Portrait (3:4)</SelectItem>
                  <SelectItem value="4:3">Landscape (4:3)</SelectItem>
                  <SelectItem value="16:9">Wide (16:9)</SelectItem>
                  <SelectItem value="9:16">Tall (9:16)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Prompt Input */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Describe Your Logo</label>
              <Textarea
                rows={4}
                placeholder="e.g., A minimalist coffee shop logo with a steaming cup, warm colors, modern style"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="resize-none"
              />
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGenerateLogo}
              disabled={isLoading || !prompt.trim()}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              size="lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Wand2 className="mr-2 h-4 w-4" />
                  Generate with AI
                </>
              )}
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or</span>
              </div>
            </div>

            {/* Upload Button */}
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="w-full"
              size="lg"
            >
              <Upload className="mr-2 h-4 w-4" />
              Upload Your Design
            </Button>

            {/* Action Buttons */}
            {generatedLogo && (
              <div className="flex gap-3 pt-4 border-t">
                <Button
                  onClick={handleSaveDesign}
                  variant="default"
                  className="flex-1"
                >
                  <Package className="mr-2 h-4 w-4" />
                  Save Design
                </Button>
                <Button
                  onClick={handleDownloadLogo}
                  variant="outline"
                  className="flex-1"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Preview Panel */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Product Preview</CardTitle>
            <CardDescription>
              See how your logo looks on the {selectedProduct}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="relative aspect-[5/6] bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg overflow-hidden shadow-inner">
              {isLoading && <LoadingSpinner />}

              {/* Product Mockup */}
              <img
                src={productMockups[selectedProduct]}
                alt={selectedProduct}
                className="w-full h-full object-cover"
              />

              {/* Logo Overlay */}
              {generatedLogo && (
                <div className="absolute inset-0 flex items-center justify-center p-12">
                  <div className="relative w-full max-w-[60%] aspect-square">
                    <img
                      src={generatedLogo}
                      alt="Generated Logo"
                      className="w-full h-full object-contain drop-shadow-2xl"
                    />
                  </div>
                </div>
              )}

              {/* Placeholder */}
              {!generatedLogo && !isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center space-y-3 p-6 bg-white/80 backdrop-blur-sm rounded-lg">
                    <Wand2 className="h-12 w-12 mx-auto text-gray-400" />
                    <p className="text-sm text-gray-600 font-medium">
                      Your logo will appear here
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
